/**
 * Service Conditions Logging
 * Handles boat conditions tracking, anode management, and historical data
 */

import { openInventoryReviewModal } from '../inventory-review-modal.js';

// Helper to capitalize first letter
function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Show notification toggle modal after successful log
function showNotificationToggleModal(conditions, logId) {
  return new Promise((resolve) => {
    const customerName = window.selectedCustomer?.name || 'Customer';
    const boatName = window.selectedBoat?.name || 'their boat';

    const modalHtml = `
      <div class="modal-overlay" id="notificationToggleModal"
           onclick="if(event.target.id === 'notificationToggleModal') window.__closeNotificationModal(false)">
        <div class="anode-modal-content" style="max-width: 480px;" onclick="event.stopPropagation()">
          <div class="anode-modal-header" style="background: #345475; color: white; padding: 16px 20px;">
            <h3 style="margin: 0; font-size: 18px;">📧 Notify Customer?</h3>
            <button onclick="window.__closeNotificationModal(false)"
                    class="anode-modal-close"
                    style="color: white; font-size: 24px;">&times;</button>
          </div>
          <div class="anode-modal-body" style="padding: 24px;">
            <p style="margin: 0 0 8px; color: #374151; font-size: 15px;">
              Service conditions for <strong>${boatName}</strong> have been logged successfully.
            </p>
            <p style="margin: 0 0 20px; color: #6b7280; font-size: 14px;">
              Would you like to send a service update email to ${customerName}?
            </p>
            <div style="display: flex; gap: 12px;">
              <button onclick="window.__closeNotificationModal(false)"
                      style="flex: 1; padding: 14px 20px; background: #f3f4f6; color: #374151;
                             border: 1px solid #d1d5db; border-radius: 8px; cursor: pointer;
                             font-size: 15px; font-weight: 500;">
                Skip
              </button>
              <button onclick="window.__closeNotificationModal(true)"
                      style="flex: 1; padding: 14px 20px; background: #345475; color: white;
                             border: none; border-radius: 8px; cursor: pointer;
                             font-size: 15px; font-weight: 500;">
                ✉️ Send Email
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    window.__closeNotificationModal = function(sendEmail) {
      const modal = document.getElementById('notificationToggleModal');
      if (modal) modal.remove();
      delete window.__closeNotificationModal;
      resolve(sendEmail);
    };
  });
}

// Send service logged email to customer
async function sendServiceLoggedEmail(conditions, logId) {
  try {
    const customer = window.selectedCustomer;
    const boat = window.selectedBoat;

    if (!customer?.email) {
      console.warn('No customer email available for notification');
      return { success: false, error: 'No customer email' };
    }

    // Get condition values from the form
    const paintCondition = document.getElementById('actualPaintCondition')?.getAttribute('data-value') ||
                          document.getElementById('wizardPaintCondition')?.value || '';
    const growthLevel = document.getElementById('actualGrowthLevel')?.getAttribute('data-value') ||
                       document.getElementById('wizardGrowthLevel')?.value || '';
    const thruHullCondition = document.getElementById('wizardThruHull')?.value || '';

    // Build boat conditions section
    const boatConditionsSection = (paintCondition || growthLevel || thruHullCondition)
      ? `<tr>
          <td style="padding: 0 40px 30px;">
            <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px; font-weight: 600;">Boat Conditions</h3>
            <table width="100%" cellpadding="8" cellspacing="0">
              ${paintCondition ? `<tr>
                <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                  <p style="margin: 0; color: #6b7280; font-size: 14px;">Paint Condition</p>
                </td>
                <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                  <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(paintCondition)}</p>
                </td>
              </tr>` : ''}
              ${growthLevel ? `<tr>
                <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                  <p style="margin: 0; color: #6b7280; font-size: 14px;">Growth Level</p>
                </td>
                <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                  <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(growthLevel)}</p>
                </td>
              </tr>` : ''}
              ${thruHullCondition ? `<tr>
                <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb;">
                  <p style="margin: 0; color: #6b7280; font-size: 14px;">Thru-Hull Condition</p>
                </td>
                <td style="padding: 12px 0; border-bottom: 1px solid #e5e7eb; text-align: right;">
                  <p style="margin: 0; color: #111827; font-size: 14px; font-weight: 600;">${capitalizeFirst(thruHullCondition.replace(/_/g, ' '))}</p>
                </td>
              </tr>` : ''}
            </table>
          </td>
        </tr>`
      : '';

    // Build anode details section from window.anodeAssignments
    const anodeArray = window.anodeAssignments ? Object.values(window.anodeAssignments) : [];
    const anodeDetailsSection = anodeArray.length > 0
      ? `<tr>
          <td style="padding: 0 40px 30px;">
            <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px;">Anode Inspection</h3>
            <table width="100%" cellpadding="8" cellspacing="0" style="border: 1px solid #e5e7eb;">
              <thead>
                <tr style="background-color: #f9fafb;">
                  <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Location</th>
                  <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Condition</th>
                  <th style="padding: 12px; text-align: left; color: #6b7280; font-size: 12px; font-weight: 600; text-transform: uppercase;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${anodeArray.map(anode => `
                  <tr>
                    <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${anode.location || 'Unknown'} - ${anode.position || ''}</td>
                    <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${anode.condition_percent || anode.condition || 'N/A'}%</td>
                    <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; color: #111827; font-size: 14px;">${capitalizeFirst(anode.status || 'inspected')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </td>
        </tr>`
      : '';

    // Build propeller section
    const propeller1 = document.querySelector('[name="propeller1Condition"]')?.value ||
                      document.getElementById('wizardPropeller1')?.value || '';
    const propeller2 = document.querySelector('[name="propeller2Condition"]')?.value ||
                      document.getElementById('wizardPropeller2')?.value || '';
    const propellerNotes = document.getElementById('propellerNotes')?.value || '';

    const propellerSection = (propeller1 || propeller2)
      ? `<tr>
          <td style="padding: 0 40px 30px;">
            <h3 style="margin: 0 0 16px; color: #1e3a8a; font-size: 18px;">Propeller Inspection</h3>
            <div style="background-color: #f9fafb; padding: 16px; border-radius: 6px;">
              ${propeller1 ? `<p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Propeller 1:</strong> ${capitalizeFirst(propeller1)}</p>` : ''}
              ${propeller2 ? `<p style="margin: 0 0 8px; color: #374151; font-size: 14px;"><strong>Propeller 2:</strong> ${capitalizeFirst(propeller2)}</p>` : ''}
              ${propellerNotes ? `<p style="margin: 8px 0 0; padding-top: 8px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px;"><strong>Notes:</strong> ${propellerNotes}</p>` : ''}
            </div>
          </td>
        </tr>`
      : '';

    // Build service logged status section (no payment - just info)
    const serviceLoggedSection = `<tr>
      <td style="padding: 0 40px 30px;">
        <div style="background: #eff6ff; border: 2px solid #3b82f6; border-radius: 8px; padding: 20px; text-align: center;">
          <div style="font-size: 24px; margin-bottom: 8px;">📋</div>
          <div style="font-size: 18px; font-weight: 600; color: #1e40af;">Service Update</div>
          <div style="font-size: 14px; color: #374151; margin-top: 8px;">
            Your boat has been inspected and the conditions have been logged.
          </div>
        </div>
      </td>
    </tr>`;

    // Send the email via server-side API proxy (service_role key stays server-side)
    const response = await fetch('/api/send-notification', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: 'service_completion',
        data: {
          customerEmail: customer.email,
          customerName: customer.name,
          boatName: boat?.name || 'Your boat',
          serviceName: conditions.service_name || conditions.service_type || 'Boat Service',
          serviceDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
          totalHours: document.getElementById('wizardTotalHours')?.textContent || '',
          invoiceNumber: '',
          invoiceTotal: '',
          boatConditionsSection: boatConditionsSection,
          anodeDetailsSection: anodeDetailsSection,
          propellerSection: propellerSection,
          pricingBreakdownHtml: '',
          paymentStatusSection: serviceLoggedSection
        },
        serviceName: 'billing'
      })
    });

    const result = await response.json();

    if (result.error) {
      console.error('❌ Failed to send service logged email:', result.error);
      return { success: false, error: result.error };
    }

    console.log('✅ Service logged email sent successfully:', result.data);
    return { success: true, data: result.data };

  } catch (error) {
    console.error('❌ Error sending service logged email:', error);
    return { success: false, error: error.message };
  }
}

export function initConditionsLogging() {
  // Helper function to save service conditions + anodes data to Portal
  window.saveServiceConditionsLog = async function(conditionsData) {
      try {
          console.log('Saving service conditions:', conditionsData);

          const { data, error } = await window.supabaseClient.functions.invoke('save-conditions', {
              body: conditionsData
          });

          if (error) {
              console.error('❌ Failed to save conditions:', error);
              console.error('📋 Error details:', error.message);
              alert(`Failed to save service log:\n${error.message}`);
              return null;
          }

          if (data && data.success) {
              console.log('✅ Conditions saved successfully:', data.condition_log_id || data.service_log_id);
              return data;
          } else {
              console.error('❌ Failed to save conditions:', data?.error);
              console.error('📋 Error details:', data?.details);
              console.error('📦 Full API response:', data);
              alert(`Failed to save service log:\n${data?.error}\n\nDetails: ${data?.details || 'No details provided'}`);
              return null;
          }
      } catch (error) {
          console.error('❌ Error saving conditions:', error);
          console.error('📋 Error message:', error.message);
          console.error('📦 Full error:', error);
          alert(`Error saving service log:\n${error.message}`);
          return null;
      }
  };

  // Fetch historical service conditions for a boat
  window.getLastServiceConditions = async function(boatId) {
      if (!boatId) {
          console.warn('No boat ID provided for historical conditions');
          return null;
      }

      try {
          console.log('Fetching last service conditions for boat:', boatId);

          const { data, error } = await window.supabaseClient
              .rpc('get_last_service_conditions', { p_boat_id: boatId });

          if (error) {
              console.error('Error fetching historical conditions:', error);
              return null;
          }

          if (data && Object.keys(data).length > 0) {
              console.log('✅ Found historical conditions:', data);
              return data;
          } else {
              console.log('No historical conditions found for this boat');
              return null;
          }
      } catch (error) {
          console.error('Error fetching historical conditions:', error);
          return null;
      }
  };

  // Display historical context for a condition field
  window.displayHistoricalContext = function(elementId, label, lastValue, lastDate) {
      const element = document.getElementById(elementId);
      if (!element) return;

      // Find or create historical context display
      let contextDiv = element.parentElement.querySelector('.historical-context');
      if (!contextDiv) {
          contextDiv = document.createElement('div');
          contextDiv.className = 'historical-context';
          element.parentElement.insertBefore(contextDiv, element);
      }

      if (lastValue && lastDate) {
          const formattedDate = new Date(lastDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
          });
          contextDiv.innerHTML = `
              <div style="font-size: 12px; color: #666; margin-bottom: 5px; font-style: italic;">
                  📋 Last ${label}: <strong>${lastValue}</strong> (${formattedDate})
              </div>
          `;
          contextDiv.style.display = 'block';
      } else {
          contextDiv.style.display = 'none';
      }
  };

  // Load and display all historical conditions for current boat
  window.loadHistoricalConditions = async function() {
      if (!window.selectedBoat || !window.selectedBoat.id) {
          console.log('No boat selected, skipping historical conditions');
          return;
      }

      const historical = await window.getLastServiceConditions(window.selectedBoat.id);
      if (!historical) return;

      // Helper function to convert paint condition number to label
      const paintNumberToLabel = (num) => {
          const labels = ['Not Inspected', 'Excellent', 'Exc-Good', 'Good', 'Good-Fair', 'Fair', 'Fair-Poor', 'Poor', 'Very Poor'];
          if (typeof num === 'number' && num >= 0 && num < labels.length) {
              return labels[num];
          }
          return null;
      };

      // Helper function to convert growth level number to label
      const growthNumberToLabel = (num) => {
          const labels = ['Not Inspected', 'Minimal', 'Min-Mod', 'Moderate', 'Mod-Heavy', 'Heavy', 'Heavy-Severe', 'Severe', 'Extreme'];
          if (typeof num === 'number' && num >= 0 && num < labels.length) {
              return labels[num];
          }
          return null;
      };

      // Display historical paint condition and set slider
      const paintCondition = historical.paint_condition_overall || historical.paint_condition;
      if (paintCondition !== null && paintCondition !== undefined) {
          const paintLastElem = document.getElementById('paintLastValue');
          if (paintLastElem) {
              // Handle numeric values, numeric strings, and text labels
              const numericValue = Number(paintCondition);
              const displayValue = !isNaN(numericValue) && typeof paintCondition !== 'object'
                  ? paintNumberToLabel(numericValue)
                  : paintCondition.charAt(0).toUpperCase() + paintCondition.slice(1).replace(/_/g, ' ');
              if (displayValue) {
                  paintLastElem.textContent = displayValue;
              }
          }

          // Store paint slider value for later React mounting
          // 10-value scale (0-9) aligned with hull-fouling-matrix.json and estimator
          const paintValueMap = {
              'not_inspected': 0,
              'excellent': 1,
              'excellent-good': 2,
              'good': 3,
              'good-fair': 4,
              'fair': 5,
              'fair-poor': 6,
              'poor': 7,
              'poor-missing': 8,
              'missing': 9,
              // Legacy mappings for backward compatibility
              'very_poor': 9
          };

          let paintSliderValue = paintValueMap[paintCondition];

          // If historical value doesn't exist in new scale, find closest match
          if (paintSliderValue === undefined) {
              paintSliderValue = 0; // Default to not inspected
          }

          // Store for React mounting
          window._historicalPaintSliderValue = paintSliderValue;
      }

      // Display historical growth level and set slider
      if (historical.growth_level !== null && historical.growth_level !== undefined) {
          const growthLastElem = document.getElementById('growthLastValue');
          if (growthLastElem) {
              // Handle numeric values, numeric strings, and text labels
              const numericValue = Number(historical.growth_level);
              const displayValue = !isNaN(numericValue) && typeof historical.growth_level !== 'object'
                  ? growthNumberToLabel(numericValue)
                  : historical.growth_level.charAt(0).toUpperCase() + historical.growth_level.slice(1).replace(/_/g, '-');
              if (displayValue) {
                  growthLastElem.textContent = displayValue;
              }
          }

          // Store growth slider value for later React mounting
          // Growth values aligned with hull-fouling-matrix.json
          const growthValues = [
              'not_inspected',
              'minimal',
              'minimal-moderate',
              'moderate',
              'moderate-heavy',
              'heavy',
              'heavy-severe',
              'severe',
              'severe-maximum'  // Was 'extreme' - aligned with matrix
          ];
          const growthSliderValue = growthValues.indexOf(historical.growth_level);
          if (growthSliderValue >= 0) {
              window._historicalGrowthSliderValue = growthSliderValue;
          } else {
              window._historicalGrowthSliderValue = 0;
          }
      }

      // Mount React with historical values if available
      if (typeof window.mountConditionsForm === 'function') {
          const paintInit = window._historicalPaintSliderValue || 0;
          const growthInit = window._historicalGrowthSliderValue || 0;
          window.mountConditionsForm('reactConditionsMount', paintInit, growthInit);
          console.log('📊 Mounted React conditions form with historical values:', { paint: paintInit, growth: growthInit });
      }

      // Display historical propeller conditions
      // Handle new format (propellers array)
      if (historical.propellers && Array.isArray(historical.propellers)) {
          historical.propellers.forEach(prop => {
              window.displayHistoricalContext(
                  `wizardPropeller${prop.number}`,
                  `Propeller ${prop.number} Condition`,
                  prop.condition + (prop.notes ? ` (${prop.notes})` : ''),
                  historical.service_date
              );
          });
      }
      // Handle legacy format (propeller_1_condition, propeller_2_condition)
      else {
          if (historical.propeller_1_condition) {
              window.displayHistoricalContext(
                  'wizardPropeller1',
                  'Propeller 1 Condition',
                  historical.propeller_1_condition,
                  historical.service_date
              );
          }

          if (historical.propeller_2_condition) {
              window.displayHistoricalContext(
                  'wizardPropeller2',
                  'Propeller 2 Condition',
                  historical.propeller_2_condition,
                  historical.service_date
              );
          }
      }

      // Store anode historical data for later use
      if (historical.anode_conditions && historical.anode_conditions.length > 0) {
          window.historicalAnodeConditions = historical.anode_conditions;
          console.log('Stored historical anode conditions:', window.historicalAnodeConditions);

          // Update anode assignment cards with historical conditions
          if (typeof window.updateHistoricalAnodeConditions === 'function') {
              window.updateHistoricalAnodeConditions();
          }
      }
  };

  // Update slider display value
  window.updateSliderDisplay = function(sliderId, displayId) {
      const slider = document.getElementById(sliderId);
      const display = document.getElementById(displayId);

      if (!slider || !display) {
          console.warn('updateSliderDisplay: Missing element', { sliderId, displayId, slider: !!slider, display: !!display });
          return;
      }

      const value = parseInt(slider.value);
      let labels, colorClass;

      if (sliderId.includes('Paint')) {
          // 10-value scale (0-9) aligned with hull-fouling-matrix.json and estimator
          labels = ['not_inspected', 'excellent', 'excellent-good', 'good', 'good-fair', 'fair', 'fair-poor', 'poor', 'poor-missing', 'missing'];
          const displayLabels = ['Not Inspected', 'Excellent', 'Exc-Good', 'Good', 'Good-Fair', 'Fair', 'Fair-Poor', 'Poor', 'Poor-Missing', 'Missing'];
          display.textContent = displayLabels[value];
          slider.setAttribute('data-value', labels[value]);
          console.log('Paint slider updated:', { value, label: displayLabels[value] });
      } else if (sliderId.includes('Growth')) {
          // Growth values aligned with hull-fouling-matrix.json
          labels = [
              'not_inspected',    // 0
              'minimal',          // 1
              'minimal-moderate', // 2
              'moderate',         // 3
              'moderate-heavy',   // 4
              'heavy',            // 5
              'heavy-severe',     // 6
              'severe',           // 7
              'severe-maximum'    // 8 - aligned with matrix
          ];
          const displayLabels = [
              'Not Inspected',    // 0
              'Minimal',          // 1
              'Minimal-Moderate', // 2
              'Moderate',         // 3
              'Moderate-Heavy',   // 4
              'Heavy',            // 5
              'Heavy-Severe',     // 6
              'Severe',           // 7
              'Severe (Maximum)'  // 8 - aligned with matrix
          ];
          display.textContent = displayLabels[value];
          slider.setAttribute('data-value', labels[value]);
          console.log('Growth slider updated:', { value, label: displayLabels[value] });
      } else if (sliderId.includes('anode')) {
          // Anode condition: 0=100%, 1=75%, 2=50%, 3=25%, 4=10%, 5=Missing
          const percents = [100, 75, 50, 25, 10, 0];
          const displayLabels = ['100%', '75%', '50%', '25%', '10%', 'Missing'];
          display.textContent = displayLabels[value];
          slider.setAttribute('data-percent', percents[value]);
      }
  };

  // Set condition for button groups
  window.setCondition = function(inputId, value, button) {
      const input = document.getElementById(inputId);
      if (input) {
          input.value = value;
          // Dispatch change event to trigger any listeners
          input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update button states
      const buttonGroup = button.parentElement;
      buttonGroup.querySelectorAll('.condition-btn').forEach(btn => {
          btn.classList.remove('active');
      });
      button.classList.add('active');

      // Trigger summary update
      if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
          window.updateWizardPricing();
      }
  };

  // Anode location management
  window.anodeAssignments = {};

  window.selectAnodeLocation = function(location) {
      // Show modal or panel for position selection
      showAnodePositionSelector(location);
  };

  window.showAnodePositionSelector = function(location) {
      const positions = getPositionsForLocation(location);

      // Create modal HTML
      const modalHtml = `
          <div class="modal-overlay" id="anodePositionModal" onclick="closeAnodeModal(event)">
              <div class="modal-content" style="max-width: 600px;" onclick="event.stopPropagation()">
                  <div class="modal-header">
                      <h3>Select Position for ${location.charAt(0).toUpperCase() + location.slice(1)}</h3>
                      <button onclick="closeAnodeModal()" style="background: none; border: none; font-size: 24px; cursor: pointer;">&times;</button>
                  </div>
                  <div class="modal-body">
                      <div class="button-group">
                          ${positions.map(pos => `
                              <button class="condition-btn" onclick="addAnodeAssignment('${location}', '${pos}')">
                                  ${pos}
                              </button>
                          `).join('')}
                      </div>
                      ${getExistingAnodesList(location)}
                  </div>
              </div>
          </div>
      `;

      document.body.insertAdjacentHTML('beforeend', modalHtml);
  };

  window.getPositionsForLocation = function(location) {
      const positionMap = {
          shaft: ['Port', 'Centerline', 'Starboard'],
          prop: ['Port', 'Centerline', 'Starboard'],
          strut: ['Port', 'Centerline', 'Starboard'],
          rudder: ['Port', 'Centerline', 'Starboard'],
          transom: ['Port', 'Centerline', 'Starboard'],
          trimtab: ['Port', 'Starboard'],
          hull: ['Port', 'Starboard'],
          keel: ['Port', 'Starboard'],
          thruster: ['Bow', 'Stern'],
          saildrive: ['Single']
      };
      return positionMap[location] || ['Port', 'Starboard'];
  };

  window.getExistingAnodesList = function(location) {
      const existing = Object.values(window.anodeAssignments).filter(a => a.location === location);
      if (existing.length === 0) return '';

      return `
          <div style="margin-top: 20px;">
              <h4>Current ${location} anodes:</h4>
              ${existing.map(anode => `
                  <div style="padding: 10px; background: #f8f9fa; border-radius: 6px; margin: 5px 0;">
                      <strong>${anode.position}</strong> - Condition: ${anode.condition || 'Not set'}
                      ${anode.replaced ? ' <span style="color: green;">✓ Replaced</span>' : ''}
                  </div>
              `).join('')}
          </div>
      `;
  };

  window.closeAnodeModal = function(event) {
      if (event && event.target !== event.currentTarget) return;
      const modal = document.getElementById('anodePositionModal');
      if (modal) modal.remove();

      // Hide the anode selector after adding an anode
      const selector = document.getElementById('anodeLocationSelector');
      const button = document.getElementById('addAnodeButton');
      if (selector && button) {
          selector.style.display = 'none';
          button.textContent = '➕ Add Anode Location';
      }
  };

  window.addAnodeAssignment = function(location, position) {
      const key = `${location}-${position}`;

      // Check if already exists
      if (window.anodeAssignments[key]) {
          alert(`An anode assignment already exists for ${location} - ${position}`);
          return;
      }

      // Add assignment
      window.anodeAssignments[key] = {
          location: location,
          position: position,
          condition: 100,
          replaced: false,
          anode_id: null,
          anode_name: null
      };

      // Update UI
      updateAnodeAssignmentsList();
      updateLocationCounts();
      closeAnodeModal();

      console.log('Added anode assignment:', window.anodeAssignments[key]);
  };

  window.updateAnodeAssignmentsList = function() {
      const listContainer = document.getElementById('anodeAssignmentsList');
      if (!listContainer) return;

      if (Object.keys(window.anodeAssignments).length === 0) {
          listContainer.innerHTML = '<p style="text-align: center; color: #666; padding: 20px;">No anode locations assigned yet. Click a location above to add one.</p>';
          return;
      }

      listContainer.innerHTML = Object.entries(window.anodeAssignments).map(([key, anode]) => `
          <div class="anode-assignment-card" id="anode-card-${key}">
              <div class="anode-assignment-header">
                  <div class="anode-assignment-title">
                      ${anode.location.charAt(0).toUpperCase() + anode.location.slice(1)} - ${anode.position}
                  </div>
                  <button class="anode-assignment-remove" onclick="removeAnodeAssignment('${key}')">
                      Remove
                  </button>
              </div>

              <div style="margin-bottom: 15px;">
                  <label style="display: block; font-weight: 500; margin-bottom: 8px;">Condition: <span id="anode-value-${key}">100%</span></label>
                  <div class="condition-button-group anode-button-group" id="anode-buttons-${key}" data-value="100">
                      <button type="button" class="condition-btn anode-btn selected" data-value="100" data-index="0" onclick="handleAnodeButtonClick('${key}', this)">100%</button>
                      <button type="button" class="condition-btn anode-btn" data-value="75" data-index="1" onclick="handleAnodeButtonClick('${key}', this)">75%</button>
                      <button type="button" class="condition-btn anode-btn" data-value="50" data-index="2" onclick="handleAnodeButtonClick('${key}', this)">50%</button>
                      <button type="button" class="condition-btn anode-btn" data-value="25" data-index="3" onclick="handleAnodeButtonClick('${key}', this)">25%</button>
                      <button type="button" class="condition-btn anode-btn" data-value="10" data-index="4" onclick="handleAnodeButtonClick('${key}', this)">10%</button>
                      <button type="button" class="condition-btn anode-btn" data-value="0" data-index="5" onclick="handleAnodeButtonClick('${key}', this)">Missing</button>
                  </div>
              </div>

              <div style="margin-bottom: 10px;">
                  <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                      <input type="checkbox" id="anode-replaced-${key}"
                             onchange="toggleAnodeReplacement('${key}', this.checked)"
                             style="width: 18px; height: 18px;">
                      <span style="font-weight: 500;">Replaced on this service</span>
                  </label>
              </div>

              <div id="anode-selector-${key}" style="display: none; margin-top: 10px; padding: 15px; background: white; border-radius: 6px;">
                  <label style="display: block; margin-bottom: 8px;">Select replacement anode:</label>
                  <select id="anode-select-${key}" class="form-control" onchange="selectReplacementAnode('${key}', this.value)">
                      <option value="">Choose an anode...</option>
                  </select>
                  <small style="color: #666; display: block; margin-top: 5px;">This will update inventory when saved</small>
              </div>
          </div>
      `).join('');

      // Load anode catalog options for each selector
      Object.keys(window.anodeAssignments).forEach(key => {
          loadAnodeCatalogForSelector(key);
      });
  };

  // Handle anode button click (single selection only)
  window.handleAnodeButtonClick = function(key, button) {
      const value = parseInt(button.getAttribute('data-value'));
      const container = document.getElementById(`anode-buttons-${key}`);

      if (!container) return;

      // Update selection
      container.querySelectorAll('.anode-btn').forEach(btn => {
          btn.classList.remove('selected');
      });
      button.classList.add('selected');

      // Update container data-value
      container.setAttribute('data-value', value);

      // Update display and data
      updateAnodeCondition(key, value);
  };

  window.updateAnodeCondition = function(key, value) {
      // Handle both numeric percent values (from buttons) and index values (legacy)
      const numValue = parseInt(value);

      // Map to display labels
      const displayMap = {
          100: '100%',
          75: '75%',
          50: '50%',
          25: '25%',
          10: '10%',
          0: 'Missing'
      };

      // If value is an index (0-5), convert to percent
      const percentValue = numValue <= 5 ?
          [100, 75, 50, 25, 10, 0][numValue] :
          numValue;

      const display = document.getElementById(`anode-value-${key}`);
      if (display) {
          display.textContent = displayMap[percentValue] || `${percentValue}%`;
      }

      if (window.anodeAssignments[key]) {
          window.anodeAssignments[key].condition = percentValue;
      }
  };

  window.toggleAnodeReplacement = function(key, isReplaced) {
      const selector = document.getElementById(`anode-selector-${key}`);
      if (selector) {
          selector.style.display = isReplaced ? 'block' : 'none';
      }

      if (window.anodeAssignments[key]) {
          window.anodeAssignments[key].replaced = isReplaced;
          if (isReplaced) {
              // Reset condition to 100% when replaced
              window.anodeAssignments[key].condition = 100;

              // Update buttons to show 100% selected
              const container = document.getElementById(`anode-buttons-${key}`);
              if (container) {
                  container.querySelectorAll('.anode-btn').forEach(btn => {
                      btn.classList.remove('selected');
                  });
                  const btn100 = container.querySelector('[data-value="100"]');
                  if (btn100) btn100.classList.add('selected');
                  container.setAttribute('data-value', '100');
              }

              updateAnodeCondition(key, 100);
          }
      }
  };

  window.selectReplacementAnode = function(key, anodeId) {
      // Store the selected anode for this location
      if (window.anodeAssignments[key]) {
          window.anodeAssignments[key].anode_id = anodeId;
          // You would fetch the anode details from your catalog here
          console.log(`Selected anode ${anodeId} for ${key}`);
      }
  };

  window.loadAnodeCatalogForSelector = function(key) {
      // Placeholder - you would load from your actual anode catalog
      // For now, just add some dummy options
      const select = document.getElementById(`anode-select-${key}`);
      if (!select) return;

      const dummyAnodes = [
          { id: 'anode1', name: 'MG Duff 1" Shaft Anode', price: 45.00 },
          { id: 'anode2', name: 'Prop Anode 2.5"', price: 55.00 },
          { id: 'anode3', name: 'Hull Anode Large', price: 35.00 }
      ];

      dummyAnodes.forEach(anode => {
          const option = document.createElement('option');
          option.value = anode.id;
          option.textContent = `${anode.name} - $${anode.price}`;
          select.appendChild(option);
      });
  };

  window.removeAnodeAssignment = function(key) {
      if (confirm('Remove this anode assignment?')) {
          delete window.anodeAssignments[key];
          updateAnodeAssignmentsList();
          updateLocationCounts();
      }
  };

  window.updateLocationCounts = function() {
      // Count anodes per location
      const counts = {};
      Object.values(window.anodeAssignments).forEach(anode => {
          counts[anode.location] = (counts[anode.location] || 0) + 1;
      });

      // Update all count badges
      ['shaft', 'prop', 'strut', 'hull', 'transom', 'rudder', 'trimtab', 'keel', 'thruster', 'saildrive'].forEach(location => {
          const countElem = document.getElementById(`count-${location}`);
          if (countElem) {
              countElem.textContent = counts[location] || 0;
          }

          // Update button active state
          const btn = document.querySelector(`[data-location="${location}"]`);
          if (btn) {
              if (counts[location] > 0) {
                  btn.classList.add('active');
              } else {
                  btn.classList.remove('active');
              }
          }
      });
  };

  // Initialize empty anode assignments
  if (!window.anodeAssignments) {
      window.anodeAssignments = {};
  }

  // Track edit mode state
  window.anodeEditMode = false;

  // Toggle anode edit mode (shows selector and remove buttons)
  window.toggleAnodeEditMode = function() {
      const selector = document.getElementById('anodeLocationSelector');
      const button = document.getElementById('editAnodeButton');
      const removeButtons = document.querySelectorAll('.anode-edit-mode-only');

      window.anodeEditMode = !window.anodeEditMode;

      if (selector && button) {
          if (window.anodeEditMode) {
              selector.style.display = 'block';
              button.textContent = '✓ Done Editing';
              button.style.background = '#00b894';
              // Show remove buttons
              removeButtons.forEach(btn => btn.style.display = 'block');
          } else {
              selector.style.display = 'none';
              button.textContent = '✏️ Edit Anode Locations';
              button.style.background = '';
              // Hide remove buttons
              removeButtons.forEach(btn => btn.style.display = 'none');
          }
      }
  };

  // Helper function to collect current conditions from form
  window.collectServiceConditions = function() {
      const conditions = {
          customer_id: window.selectedCustomer?.id || null,
          boat_id: window.selectedBoat?.id || null,
          service_id: window.selectedRecurringService?.id || null,
          service_type: window.currentServiceKey || '',
          service_name: window.selectedRecurringService?.service_name || '',
          service_date: window.serviceDate || new Date().toISOString().split('T')[0],
          start_time: window.startTime || null,
          end_time: window.endTime || null,
          service_time: window.startTime || new Date().toTimeString().split(' ')[0]
      };

      // Collect paint condition from slider
      const paintSlider = document.getElementById('actualPaintCondition');
      if (paintSlider) {
          const paintValue = paintSlider.getAttribute('data-value');
          if (paintValue) {
              conditions.paint_condition_overall = paintValue;
          }
      } else {
          // Fallback to old inputs
          const paintCondition = document.getElementById('wizardPaintCondition')?.value ||
                                document.getElementById('paintCondition')?.value;
          if (paintCondition) {
              conditions.paint_condition_overall = paintCondition;
          }
      }

      // Collect growth level from slider
      const growthSlider = document.getElementById('actualGrowthLevel');
      if (growthSlider) {
          const growthValue = growthSlider.getAttribute('data-value');
          if (growthValue) {
              conditions.growth_level = growthValue;
          }
      } else {
          // Fallback to old inputs
          const growthLevel = document.getElementById('wizardGrowthLevel')?.value ||
                             document.getElementById('growthLevel')?.value;
          if (growthLevel) {
              conditions.growth_level = growthLevel;
          }
      }

      // Collect anode conditions from new anode assignments system
      const anodeConditions = [];
      const anodesInstalled = [];

      if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
          Object.values(window.anodeAssignments).forEach(anode => {
              // Add condition before service
              anodeConditions.push({
                  location: anode.location,
                  position: anode.position,
                  condition_percent: anode.condition,
                  checked_date: new Date().toISOString()
              });

              // Track all replacement types (replaced, retrieve, order)
              // All represent anodes that need to be installed/replaced, just with different workflows
              if (anode.status === 'replaced' || anode.status === 'retrieve' || anode.status === 'order') {
                  // Generate a descriptive fallback name if none exists
                  const location = anode.location || 'unknown';
                  const position = anode.position || 'unknown';
                  const fallbackName = `Anode (${location} - ${position})`;
                  const anodeName = anode.anode_name || fallbackName;

                  anodesInstalled.push({
                      anode_id: anode.anode_id || null,
                      anode_name: anodeName,  // Guaranteed to have a value
                      catalog_id: anode.anode_id || null,  // catalog_id same as anode_id (from boat data)
                      location: anode.location,
                      position: anode.position,
                      quantity: 1,
                      condition_before: anode.condition,
                      unit_price: anode.anode_price || null,  // Use anode_price from boat data
                      status: anode.status,  // IMPORTANT: Preserves status for downstream actions
                      replaced_on_service: true,
                      inventory_id: anode.inventory_id || null
                  });

                  // TODO: Trigger status-specific actions after save
                  // - 'replaced': Remove from inventory, charge customer (handled by pricing logic)
                  // - 'retrieve': Trigger reminder/flag system (NOT IMPLEMENTED YET)
                  // - 'order': Add to order list, flag in inventory (NOT IMPLEMENTED YET)
              }
          });
      }

      // Also collect from old anode cart system if present
      if (window.anodeCart && Object.keys(window.anodeCart).length > 0) {
          Object.values(window.anodeCart).forEach(anode => {
              // Ensure we have a name, with fallback
              const anodeName = anode.name || anode.anode_name || 'Anode (catalog item)';

              anodesInstalled.push({
                  id: anode.id,
                  name: anodeName,  // Guaranteed to have a value
                  quantity: anode.quantity,
                  unit_price: anode.list_price,
                  inventory_id: anode.inventory_id
              });
          });
      }

      if (anodeConditions.length > 0) {
          conditions.anode_conditions = anodeConditions;
      }

      if (anodesInstalled.length > 0) {
          conditions.anodes_installed = anodesInstalled;
      }

      // Collect propeller conditions (up to 5 propellers)
      const propellers = [];
      for (let i = 1; i <= 5; i++) {
          const condition = document.getElementById(`wizardPropeller${i}`)?.value;
          const notes = document.getElementById(`wizardPropeller${i}Notes`)?.value || '';

          if (condition && condition !== '' && condition !== 'not_inspected') {
              propellers.push({
                  number: i,
                  condition: condition,
                  notes: notes
              });
          }
      }

      if (propellers.length > 0) {
          conditions.propellers = propellers;
      }

      const propNotes = document.getElementById('wizardPropellerNotes')?.value;
      if (propNotes) {
          conditions.propeller_notes = propNotes;
      }

      return conditions;
  };

  // Log conditions without charging customer
  window.logConditionsOnly = async function() {
      try {
          const logButton = document.getElementById('logOnlyButton');
          if (!logButton) return;

          // Validate required fields first
          const conditions = window.collectServiceConditions();

          if (!conditions.customer_id) {
              alert('⚠️ No customer selected. Please select a customer first.');
              return;
          }

          if (!conditions.service_type) {
              alert('⚠️ No service type selected. Please select a service first.');
              return;
          }

          // ===== SHOW INVENTORY REVIEW MODAL (same as Charge Customer) =====
          // Check if there are anodes to process
          const anodeArray = window.anodeAssignments ? Object.values(window.anodeAssignments) : [];
          if (anodeArray.length > 0) {
              const hasInventoryActions = anodeArray.some(a =>
                  a.status === 'replaced' || a.status === 'retrieve' || a.status === 'order'
              );

              if (hasInventoryActions) {
                  try {
                      // Show modal and wait for confirmation
                      await new Promise((resolve, reject) => {
                          openInventoryReviewModal(anodeArray, () => {
                              console.log('✅ Inventory review confirmed for Log Only');
                              resolve();
                          });
                      });
                  } catch (error) {
                      console.error('Inventory review modal error:', error);
                      // Continue anyway - inventory processing will happen in background
                  }
              }
          }
          // ===== END INVENTORY REVIEW MODAL =====

          // Disable button and show loading state
          const originalText = logButton.innerHTML;
          logButton.disabled = true;
          logButton.innerHTML = '⏳ Logging...';

          // Save conditions to database
          const result = await window.saveServiceConditionsLog(conditions);

          // Also save anode assignments to boat record for persistence
          if (result && result.success && window.selectedBoat?.id && window.anodeAssignments) {
              await window.saveBoatAnodes(window.selectedBoat.id, window.anodeAssignments);
          }

          // ===== INVENTORY PROCESSING (same as Charge Customer flow) =====
          // Process inventory actions for anodes (replaced, retrieve, order)
          // Note: inventory_id was already attached by the modal's fetchInventoryLevels()
          const anodeArrayForProcessing = window.anodeAssignments ? Object.values(window.anodeAssignments) : [];
          if (result && result.success && anodeArrayForProcessing.length > 0) {
              const boatId = window.selectedBoat?.id || null;
              const serviceLogId = result.condition_log_id || result.service_log_id || 'temp-' + Date.now();

              if (boatId) {
                  console.log('Processing inventory for Log Only with anodes:', anodeArrayForProcessing);

                  // Prepare anodes payload with inventory IDs (already attached by modal)
                  const anodesPayload = anodeArrayForProcessing.map(anode => ({
                      status: anode.status,
                      anode_id: anode.anode_id,
                      anode_inventory_id: anode.inventory_id,
                      anode_name: anode.anode_name
                  }));

                  try {
                      const { data: inventoryResult, error: inventoryError } = await window.supabaseClient.functions.invoke('finalize-service-inventory', {
                          body: {
                              serviceLogId: serviceLogId,
                              boatId: boatId,
                              anodes: anodesPayload
                          }
                      });

                      if (inventoryError) {
                          console.error('❌ Error calling finalize-service-inventory:', inventoryError);
                          console.warn('⚠️ Inventory processing failed - service logged anyway');
                      } else if (inventoryResult && inventoryResult.success) {
                          console.log('✅ Inventory actions processed for Log Only:', inventoryResult.summary);
                      } else {
                          console.error('❌ Inventory processing failed:', inventoryResult);
                          if (inventoryResult?.results?.errors) {
                              console.error('   Inventory errors:', inventoryResult.results.errors);
                          }
                      }
                  } catch (err) {
                      console.error('❌ Error calling finalize-service-inventory:', err);
                      console.warn('⚠️ Inventory processing failed - service logged anyway');
                  }
              } else {
                  console.warn('⚠️ Boat ID not found - skipping inventory processing');
              }
          }
          // ===== END INVENTORY PROCESSING =====

          if (result && result.success) {
              // Reset button first
              logButton.disabled = false;
              logButton.innerHTML = originalText;

              console.log('✅ Conditions logged successfully (no charge)');

              // Show notification toggle modal
              const logId = result.condition_log_id || result.service_log_id;
              const sendEmail = await showNotificationToggleModal(conditions, logId);

              let emailStatus = '';
              if (sendEmail) {
                  // User chose to send email
                  const emailResult = await sendServiceLoggedEmail(conditions, logId);
                  if (emailResult.success) {
                      emailStatus = `<p style="margin-top: 15px; color: #16a34a;">✉️ Service update email sent to ${window.selectedCustomer?.email}</p>`;
                  } else {
                      emailStatus = `<p style="margin-top: 15px; color: #dc2626;">⚠️ Failed to send email: ${emailResult.error}</p>`;
                  }
              } else {
                  emailStatus = `<p style="margin-top: 15px; color: #666;">No email sent to customer.</p>`;
              }

              // Show success message
              const resultDiv = document.getElementById('chargeResult');
              if (resultDiv) {
                  resultDiv.className = 'charge-result success';
                  resultDiv.innerHTML = `
                      <h3>✅ Service Conditions Logged Successfully</h3>
                      <p>Service conditions have been saved to Boat History in Operations.</p>
                      <p><strong>Customer:</strong> ${window.selectedCustomer?.name || 'N/A'}</p>
                      <p><strong>Boat:</strong> ${window.selectedBoat?.name || 'N/A'}</p>
                      <p><strong>Service:</strong> ${conditions.service_name || conditions.service_type}</p>
                      <p><strong>Log ID:</strong> ${logId}</p>
                      <p style="margin-top: 15px; color: #666;">No payment was charged.</p>
                      ${emailStatus}
                  `;
                  resultDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }
          } else {
              // Show error
              const resultDiv = document.getElementById('chargeResult');
              if (resultDiv) {
                  resultDiv.className = 'charge-result error';
                  resultDiv.innerHTML = `
                      <h3>❌ Failed to Log Conditions</h3>
                      <p>There was an error saving the service conditions. Please try again.</p>
                  `;
                  resultDiv.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }

              // Reset button
              logButton.disabled = false;
              logButton.innerHTML = originalText;

              console.error('❌ Failed to log conditions');
          }
      } catch (error) {
          console.error('Error in logConditionsOnly:', error);

          const logButton = document.getElementById('logOnlyButton');
          if (logButton) {
              logButton.disabled = false;
              logButton.innerHTML = '📋 Log Only (No Charge)';
          }

          const resultDiv = document.getElementById('chargeResult');
          if (resultDiv) {
              resultDiv.className = 'charge-result error';
              resultDiv.innerHTML = `
                  <h3>❌ Error</h3>
                  <p>An unexpected error occurred. Please try again.</p>
              `;
          }
      }
  };

  // Wrap the existing charge customer function to add condition logging
  // This will be called after successful payment
  window.addEventListener('DOMContentLoaded', function() {
      // Wait for adminApp to be ready
      const checkAdminApp = setInterval(() => {
          if (window.adminApp && window.adminApp.chargeCustomer) {
              // Store original chargeCustomer function
              const originalChargeCustomer = window.adminApp.chargeCustomer.bind(window.adminApp);

              // Wrap with condition logging (logging only - saves handled by enhanced-charge-flow.js)
              window.adminApp.chargeCustomer = async function() {
                  console.log('Enhanced chargeCustomer called - will log conditions');

                  // Call original charge function (which handles save-conditions internally)
                  const result = await originalChargeCustomer();

                  // NOTE: Condition saving is now handled inside enhanced-charge-flow.js
                  // to avoid duplicate service_logs entries. See line ~803 in enhanced-charge-flow.js.

                  return result;
              };

              clearInterval(checkAdminApp);
              console.log('✅ Condition logging integrated into charge flow');
          }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => clearInterval(checkAdminApp), 10000);

      // Add event listeners for condition fields to trigger summary updates
      // Thru-hull hidden input (updated by setCondition function)
      const thruHullInput = document.getElementById('wizardThruHull');
      if (thruHullInput) {
          thruHullInput.addEventListener('change', function() {
              if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
                  window.updateWizardPricing();
              }
          });
      }

      // Notes text areas
      const notesFields = ['wizardThruHullNotes', 'wizardPropellerNotes', 'wizardGeneralNotes'];
      notesFields.forEach(fieldId => {
          const field = document.getElementById(fieldId);
          if (field) {
              field.addEventListener('input', function() {
                  if (window.updateWizardPricing && typeof window.updateWizardPricing === 'function') {
                      window.updateWizardPricing();
                  }
              });
          }
      });
  });

  // Function to update propeller condition fields based on boat propeller count
  window.updatePropellerConditionFields = function() {
      const propellerCount = window.selectedBoat?.propeller_count;

      console.log('🔧 Updating propeller condition fields for count:', propellerCount);

      // Show/hide propeller 2 condition based on count
      const prop2Group = document.getElementById('propeller2ConditionGroup');
      if (prop2Group) {
          if (propellerCount >= 2) {
              prop2Group.style.display = 'block';
              console.log('  ✅ Showing Propeller 2 condition');
          } else {
              prop2Group.style.display = 'none';
              console.log('  ⚠️ Hiding Propeller 2 condition');
          }
      }

      // Hide propeller 1 if count is 0
      const prop1Group = document.querySelector('.condition-group:has(#wizardPropeller1)');
      if (prop1Group) {
          if (propellerCount === 0) {
              prop1Group.style.display = 'none';
              console.log('  ⚠️ Hiding Propeller 1 condition (no propellers)');
          } else {
              prop1Group.style.display = 'block';
              console.log('  ✅ Showing Propeller 1 condition');
          }
      }
  };

  console.log('✅ Conditions logging initialized');
}
